import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Receipt, FileText, AlertCircle, CheckCircle2, Clock, DollarSign, Plus } from "lucide-react";
import { motion } from "framer-motion";
import { PageHeader, Badge, Spinner } from "../components/Brand";
import { useApp, API } from "../AppContext";

const fmt = (n) => new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(n || 0);

const STATUS_META = {
  paid: { color: "#4ADE80", bg: "rgba(74,222,128,0.12)", icon: CheckCircle2 },
  partial: { color: "#D4AF37", bg: "rgba(212,175,55,0.12)", icon: Clock },
  unpaid: { color: "#7BAEFA", bg: "rgba(123,174,250,0.12)", icon: FileText },
  overdue: { color: "#C00000", bg: "rgba(192,0,0,0.18)", icon: AlertCircle },
};

export default function InvoicesList() {
  const { lang } = useApp();
  const [list, setList] = useState(null);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    axios.get(`${API}/invoices`).then(({ data }) => setList(data));
    axios.get(`${API}/dashboard/invoices-summary`).then(({ data }) => setSummary(data));
  }, []);

  const T = lang === "fr" ? {
    title: "Factures", sub: "Suivi des paiements et créances.",
    all: "Toutes", paid: "Payées", partial: "Partielles", unpaid: "Impayées", overdue: "En retard",
    paid_total: "Encaissé", open_total: "À recevoir", overdue_total: "En retard",
    no_data: "Aucune facture pour l'instant.",
    no_data_hint: "Convertissez une soumission acceptée en facture depuis son écran.",
    client: "Client", project: "Projet", total: "Total", paid_lbl: "Payé", balance: "Solde", due: "Échéance",
  } : {
    title: "Invoices", sub: "Payments and receivables.",
    all: "All", paid: "Paid", partial: "Partial", unpaid: "Unpaid", overdue: "Overdue",
    paid_total: "Collected", open_total: "Receivable", overdue_total: "Overdue",
    no_data: "No invoices yet.",
    no_data_hint: "Convert an accepted quote into an invoice from its screen.",
    client: "Client", project: "Project", total: "Total", paid_lbl: "Paid", balance: "Balance", due: "Due",
  };

  if (!list || !summary) return <Spinner />;

  const filtered = filter === "all" ? list : list.filter((i) => i.status === filter);

  return (
    <div data-testid="invoices-page">
      <PageHeader title={T.title} subtitle={T.sub} />

      {/* Cash-flow snapshot */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 max-w-5xl">
        <SummaryCard color="#4ADE80" icon={CheckCircle2} label={T.paid_total} value={fmt(summary.paid_total)} testid="sum-paid" />
        <SummaryCard color="#D4AF37" icon={DollarSign} label={T.open_total} value={fmt(summary.open_total)} testid="sum-open" />
        <SummaryCard color="#C00000" icon={AlertCircle} label={T.overdue_total} value={fmt(summary.overdue_total)} testid="sum-overdue" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4 max-w-5xl" data-testid="invoice-filters">
        {["all", "unpaid", "partial", "paid", "overdue"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s}`}
            className="px-4 py-2 rounded-full text-xs uppercase tracking-wider transition"
            style={{
              background: filter === s ? "rgba(212,175,55,0.15)" : "rgba(245,245,245,0.04)",
              color: filter === s ? "#D4AF37" : "rgba(245,245,245,0.6)",
              border: `1px solid ${filter === s ? "rgba(212,175,55,0.4)" : "rgba(245,245,245,0.08)"}`,
            }}>
            {T[s]} {s !== "all" && `(${summary.count_by_status[s] || 0})`}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="glass-card p-10 text-center max-w-5xl" data-testid="invoice-empty">
          <Receipt size={48} color="rgba(245,245,245,0.3)" className="mx-auto mb-4" />
          <p className="text-sm" style={{ color: "rgba(245,245,245,0.7)" }}>{T.no_data}</p>
          <p className="text-xs mt-2" style={{ color: "rgba(245,245,245,0.5)" }}>{T.no_data_hint}</p>
        </div>
      ) : (
        <div className="space-y-2 max-w-5xl">
          {filtered.map((inv, i) => {
            const meta = STATUS_META[inv.status] || STATUS_META.unpaid;
            const Icon = meta.icon;
            const balance = (inv.total || 0) - (inv.amount_paid || 0);
            return (
              <motion.div key={inv.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}>
                <Link to={`/app/invoices/${inv.id}`} className="block glass-card p-4 hover:brightness-110 transition" data-testid={`invoice-row-${inv.id}`}>
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: meta.bg }}>
                      <Icon size={16} color={meta.color} />
                    </div>
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading text-base font-bold" style={{ color: "#D4AF37" }}>{inv.invoice_number}</span>
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: meta.bg, color: meta.color }}>
                          {T[inv.status]}
                        </span>
                      </div>
                      <div className="text-sm mt-0.5" style={{ color: "#F5F5F5" }}>{inv.client_name} {inv.project_name && <span style={{ color: "rgba(245,245,245,0.5)" }}>· {inv.project_name}</span>}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-heading text-lg font-bold" style={{ color: "#F5F5F5" }}>{fmt(inv.total)}</div>
                      {inv.amount_paid > 0 && (
                        <div className="text-xs" style={{ color: "rgba(245,245,245,0.55)" }}>
                          {fmt(inv.amount_paid)} {T.paid_lbl.toLowerCase()} · {fmt(balance)} {T.balance.toLowerCase()}
                        </div>
                      )}
                      {inv.due_date && (
                        <div className="text-xs" style={{ color: inv.status === "overdue" ? "#FF6B6B" : "rgba(245,245,245,0.45)" }}>
                          {T.due}: {new Date(inv.due_date).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA")}
                        </div>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="glass-card p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} color={color} />
        <div className="text-xs uppercase tracking-wider" style={{ color: "rgba(245,245,245,0.55)" }}>{label}</div>
      </div>
      <div className="font-heading text-2xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}
